import React, { useState, useEffect, useCallback, Suspense, type ReactNode } from 'react';
import { clearChunkReloadFlag, lazyWithRetry } from './utils/lazyWithRetry';
import { canAccessTab, firstAccessibleTab, firstOpenTab } from './utils/access';
import { clearSessionAccess, setSessionAccess } from './utils/accessSession';
import { defaultTabForRole, readStoredUser } from './utils/safeUser';
import api, { invalidateApiCache } from './services/api';
import { useDataSyncRefresh } from './hooks/useDataSyncRefresh';
import ModuleErrorBoundary from './components/ModuleErrorBoundary';
import { AppDialogProvider } from './context/AppDialogContext';
import './App.css';
import logo from './logo.png';
import { LayoutDashboard, Box, Settings as SettingsIcon, BarChart3, Wallet, ShoppingBag, LogOut, User as UserIcon, Menu, X, Users, ChevronDown, ChevronUp, FolderKanban, LineChart } from 'lucide-react';

// Lazy loaded components (auto-reload once if cached chunks are stale after deploy)
const Dashboard = lazyWithRetry(() => import('./components/Dashboard'));
const DataAnalysis = lazyWithRetry(() => import('./components/DataAnalysis'));
const Inventory = lazyWithRetry(() => import('./components/Inventory'));
const Production = lazyWithRetry(() => import('./components/Production'));
const QualityControl = lazyWithRetry(() => import('./components/QualityControl'));
const Expenses = lazyWithRetry(() => import('./components/Expenses'));
const FinanceAnalysis = lazyWithRetry(() => import('./components/FinanceAnalysis'));
const Payroll = lazyWithRetry(() => import('./components/Payroll'));
const Attendance = lazyWithRetry(() => import('./components/Attendance'));
const VerifyBatch = lazyWithRetry(() => import('./components/VerifyBatch'));
const Settings = lazyWithRetry(() => import('./components/Settings'));
const Orders = lazyWithRetry(() => import('./components/Orders'));
const Customers = lazyWithRetry(() => import('./components/Customers'));
const HR = lazyWithRetry(() => import('./components/HR'));
const Leave = lazyWithRetry(() => import('./components/Leave'));
const Vouchers = lazyWithRetry(() => import('./components/Vouchers'));
const Projects = lazyWithRetry(() => import('./components/Projects'));
const Login = lazyWithRetry(() => import('./components/Login'));
const SetupPassword = lazyWithRetry(() => import('./components/SetupPassword'));
const ForgotPassword = lazyWithRetry(() => import('./components/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('./components/ResetPassword'));

const MODULE_FALLBACK = (
  <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>Loading application module...</div>
);

/** Keep visited tabs mounted (hidden) so completed actions stay on screen without a full reload. */
function KeepAlivePage({
  tab,
  activeTab,
  children,
}: {
  tab: string;
  activeTab: string;
  children: ReactNode;
}) {
  const [visited, setVisited] = useState(() => tab === activeTab);
  useEffect(() => {
    if (activeTab === tab) setVisited(true);
  }, [activeTab, tab]);
  if (!visited) return null;
  const shown = activeTab === tab;
  return (
    <div hidden={!shown} aria-hidden={!shown} className="keep-alive-page">
      <ModuleErrorBoundary>
        <Suspense fallback={MODULE_FALLBACK}>{children}</Suspense>
      </ModuleErrorBoundary>
    </div>
  );
}

function AppInner() {
  const [activeTab, setActiveTab] = useState(() => defaultTabForRole(readStoredUser()?.role));
  const [user, setUser] = useState<any>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(localStorage.getItem('sidebar_collapsed') === 'true');
  const [isFinancialsOpen, setIsFinancialsOpen] = useState(false);
  const [isHrOpen, setIsHrOpen] = useState(false);
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const [isProductionOpen, setIsProductionOpen] = useState(false);
  const [accessTick, setAccessTick] = useState(0);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', isSidebarCollapsed.toString());
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (!user) return;
    if (!canAccessTab(user.role, activeTab)) {
      setActiveTab(firstOpenTab(user.role));
    }
  }, [user, activeTab, accessTick]);

  const loadAccess = useCallback(() => {
    if (!user?.role) {
      clearSessionAccess();
      return;
    }
    api
      .get('/access/me')
      .then((res) => {
        setSessionAccess(user.role, res.data?.pages || [], res.data?.actions || []);
        setAccessTick((tick) => tick + 1);
      })
      .catch(() => undefined);
  }, [user]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);
  useDataSyncRefresh(['access'], loadAccess);

  useEffect(() => {
    if (activeTab === 'expenses' || activeTab === 'payroll' || activeTab === 'financeAnalysis') {
      setIsFinancialsOpen(true);
    }
    if (activeTab === 'hr' || activeTab === 'leave' || activeTab === 'attendance') {
      setIsHrOpen(true);
    }
    if (activeTab === 'inventory' || activeTab === 'vouchers') {
      setIsInventoryOpen(true);
    }
    if (activeTab === 'production' || activeTab === 'qc') {
      setIsProductionOpen(true);
    }
  }, [activeTab]);

  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  useEffect(() => {
    const storedColor = localStorage.getItem('app_theme_color');
    if (storedColor) {
      document.documentElement.style.setProperty('--primary-color', storedColor);
    }
    
    const storedUser = readStoredUser();
    if (storedUser) {
      setUser(storedUser);
    } else if (localStorage.getItem('trashcoin_user')) {
      localStorage.removeItem('trashcoin_user');
    }
  }, []);

  const handleLogin = (userData: any) => {
    invalidateApiCache();
    setUser(userData);
    localStorage.setItem('trashcoin_user', JSON.stringify(userData));
    if (userData.role === 'ADMINISTRATIVE' || userData.role === 'USER') {
      setActiveTab(defaultTabForRole(userData.role));
    }
  };

  const handleLogout = () => {
    invalidateApiCache();
    clearSessionAccess();
    setAccessTick((tick) => tick + 1);
    setUser(null);
    localStorage.removeItem('trashcoin_user');
  };

  const handleNavClick = (tab: string) => {
    if (!canAccessTab(user?.role, tab)) {
      return;
    }
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  if (window.location.pathname.startsWith('/verify/')) {
    const certNumber = window.location.pathname.split('/verify/')[1];
    return <Suspense fallback={<div style={{ textAlign: 'center', padding: '3rem' }}>Verifying...</div>}><VerifyBatch certNumber={certNumber} /></Suspense>;
  }

  if (window.location.pathname.replace(/\/$/, '') === '/setup-password') {
    return <Suspense fallback={<div>Loading setup...</div>}><SetupPassword /></Suspense>;
  }

  if (window.location.pathname.replace(/\/$/, '') === '/forgot-password') {
    return <Suspense fallback={<div>Loading...</div>}><ForgotPassword /></Suspense>;
  }

  if (window.location.pathname.replace(/\/$/, '') === '/reset-password') {
    return <Suspense fallback={<div>Loading...</div>}><ResetPassword /></Suspense>;
  }

  if (!user) {
    return <Suspense fallback={<div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading Portal...</div>}><Login onLogin={handleLogin} /></Suspense>;
  }

  return (
    <div className="app-container">
      {isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setIsMobileMenuOpen(false)}></div>
      )}
      
      <aside className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          {/* Desktop Collapse Toggle */}
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            style={{ 
              position: 'absolute', 
              top: '0.5rem', 
              right: '0.5rem',
              background: 'rgba(255,255,255,0.05)', 
              border: 'none', 
              color: 'rgba(255,255,255,0.6)', 
              padding: '0.35rem', 
              borderRadius: '6px', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <Menu size={14} />
          </button>
          
          <div className="brand-section">
            <img 
              src={logo} 
              alt="Logo" 
              className="brand-logo"
            />
            
            <div className="brand-text">
              <h2>Trashcoin</h2>
              <p>Facility Ops</p>
            </div>
          </div>
          
          <button className="mobile-close-btn" onClick={() => setIsMobileMenuOpen(false)}>
            <X size={24} color="white" />
          </button>
        </div>
        <nav className="sidebar-nav">
          {canAccessTab(user.role, 'dashboard') && (
            <button data-e2e="nav-dashboard" className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => handleNavClick('dashboard')}>
              <LayoutDashboard size={20} /> <span>Dashboard</span>
            </button>
          )}
          {canAccessTab(user.role, 'analysis') && (
            <button data-e2e="nav-analysis" className={activeTab === 'analysis' ? 'active' : ''} onClick={() => handleNavClick('analysis')}>
              <LineChart size={20} /> <span>Data Analysis</span>
            </button>
          )}
          {firstAccessibleTab(user.role, ['inventory', 'vouchers']) && (
            <div className="nav-dropdown-group" style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                data-e2e="nav-inventory"
                className={(activeTab === 'inventory' || activeTab === 'vouchers') ? 'active' : ''}
                onClick={() => {
                  setIsInventoryOpen(!isInventoryOpen);
                  const dest = firstAccessibleTab(user.role, ['inventory', 'vouchers']);
                  if (dest) handleNavClick(dest);
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Box size={20} /> <span>Inventory</span>
                </div>
                {!isSidebarCollapsed && (isInventoryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </button>
              {isInventoryOpen && !isSidebarCollapsed && (
                <div className="nav-dropdown-items">
                  {canAccessTab(user.role, 'inventory') && (
                    <button data-e2e="nav-inventory-stock" className={activeTab === 'inventory' ? 'active-sub' : ''} onClick={() => handleNavClick('inventory')}>
                      Stock
                    </button>
                  )}
                  {canAccessTab(user.role, 'vouchers') && (
                    <button data-e2e="nav-vouchers" className={activeTab === 'vouchers' ? 'active-sub' : ''} onClick={() => handleNavClick('vouchers')}>
                      Agent Vouchers
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {canAccessTab(user.role, 'projects') && (
            <button data-e2e="nav-projects" className={activeTab === 'projects' ? 'active' : ''} onClick={() => handleNavClick('projects')}>
              <FolderKanban size={20} /> <span>Projects</span>
            </button>
          )}
          {firstAccessibleTab(user.role, ['production', 'qc']) && (
            <div className="nav-dropdown-group" style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                data-e2e="nav-production"
                className={(activeTab === 'production' || activeTab === 'qc') ? 'active' : ''}
                onClick={() => {
                  setIsProductionOpen(!isProductionOpen);
                  const dest = firstAccessibleTab(user.role, ['production', 'qc']);
                  if (dest) handleNavClick(dest);
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <BarChart3 size={20} /> <span>Production</span>
                </div>
                {!isSidebarCollapsed && (isProductionOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </button>
              {isProductionOpen && !isSidebarCollapsed && (
                <div className="nav-dropdown-items">
                  {canAccessTab(user.role, 'production') && (
                    <button data-e2e="nav-production-floor" className={activeTab === 'production' ? 'active-sub' : ''} onClick={() => handleNavClick('production')}>
                      Production
                    </button>
                  )}
                  {canAccessTab(user.role, 'qc') && (
                    <button data-e2e="nav-qc" className={activeTab === 'qc' ? 'active-sub' : ''} onClick={() => handleNavClick('qc')}>
                      Quality Control
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {canAccessTab(user.role, 'orders') && (
            <button data-e2e="nav-orders" className={activeTab === 'orders' ? 'active' : ''} onClick={() => handleNavClick('orders')}>
              <ShoppingBag size={20} /> <span>Sales Order</span>
            </button>
          )}
          {canAccessTab(user.role, 'customers') && (
            <button data-e2e="nav-customers" className={activeTab === 'customers' ? 'active' : ''} onClick={() => handleNavClick('customers')}>
              <Users size={20} /> <span>Customers</span>
            </button>
          )}
          {firstAccessibleTab(user.role, ['hr', 'leave', 'attendance']) && (
            <div className="nav-dropdown-group" style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                data-e2e="nav-hr-toggle"
                className={(activeTab === 'hr' || activeTab === 'leave' || activeTab === 'attendance') ? 'active' : ''}
                onClick={() => {
                  setIsHrOpen(!isHrOpen);
                  const dest = firstAccessibleTab(user.role, ['hr', 'leave', 'attendance']);
                  if (dest) handleNavClick(dest);
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Users size={20} /> <span>HR</span>
                </div>
                {!isSidebarCollapsed && (isHrOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </button>
              {isHrOpen && !isSidebarCollapsed && (
                <div className="nav-dropdown-items">
                  {canAccessTab(user.role, 'hr') && (
                    <button data-e2e="nav-hr" className={activeTab === 'hr' ? 'active-sub' : ''} onClick={() => handleNavClick('hr')}>
                      Team
                    </button>
                  )}
                  {canAccessTab(user.role, 'leave') && (
                    <button data-e2e="nav-leave" className={activeTab === 'leave' ? 'active-sub' : ''} onClick={() => handleNavClick('leave')}>
                      Leave
                    </button>
                  )}
                  {canAccessTab(user.role, 'attendance') && (
                    <button data-e2e="nav-attendance" className={activeTab === 'attendance' ? 'active-sub' : ''} onClick={() => handleNavClick('attendance')}>
                      Attendance
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {firstAccessibleTab(user.role, ['expenses', 'payroll', 'financeAnalysis']) && (
            <div className="nav-dropdown-group" style={{ display: 'flex', flexDirection: 'column' }}>
              <button
                data-e2e="nav-financials-toggle"
                className={(activeTab === 'expenses' || activeTab === 'payroll' || activeTab === 'financeAnalysis') ? 'active' : ''}
                onClick={() => {
                  setIsFinancialsOpen(!isFinancialsOpen);
                  const dest = firstAccessibleTab(user.role, ['expenses', 'payroll', 'financeAnalysis']);
                  if (dest) handleNavClick(dest);
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Wallet size={20} /> <span>Financials</span>
                </div>
                {!isSidebarCollapsed && (isFinancialsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
              </button>
              {isFinancialsOpen && !isSidebarCollapsed && (
                <div className="nav-dropdown-items">
                  {canAccessTab(user.role, 'expenses') && (
                    <button data-e2e="nav-expenses" className={activeTab === 'expenses' ? 'active-sub' : ''} onClick={() => handleNavClick('expenses')}>
                      OPEX/REVENUE
                    </button>
                  )}
                  {canAccessTab(user.role, 'payroll') && (
                    <button data-e2e="nav-payroll" className={activeTab === 'payroll' ? 'active-sub' : ''} onClick={() => handleNavClick('payroll')}>
                      Payroll Management
                    </button>
                  )}
                  {canAccessTab(user.role, 'financeAnalysis') && (
                    <button data-e2e="nav-finance-analysis" className={activeTab === 'financeAnalysis' ? 'active-sub' : ''} onClick={() => handleNavClick('financeAnalysis')}>
                      Financial Analysis
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          <button data-e2e="nav-settings" className={activeTab === 'settings' ? 'active' : ''} onClick={() => handleNavClick('settings')}>
            <SettingsIcon size={20} /> <span>Settings</span>
          </button>
        </nav>
        
        <div style={{ marginTop: 'auto', padding: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
           <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.4rem', borderRadius: '50%' }}>
                <UserIcon size={16} />
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p style={{ fontSize: '0.85rem', fontWeight: '700', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.full_name}</p>
                <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>{user.role}</p>
              </div>
           </div>
           <button onClick={handleLogout} style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }}>
             <LogOut size={16} /> Logout
           </button>
           {process.env.REACT_APP_BUILD_TIME && (
             <p
               data-testid="ui-build-stamp"
               title="If this time is old after a deploy, run npm run local:docker from the repo root"
               style={{ margin: '0.75rem 0 0', fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.3 }}
             >
               UI build {process.env.REACT_APP_BUILD_TIME}
             </p>
           )}
        </div>
      </aside>
      
      <main className="main-content">
        <header className="top-bar">
          <div className="top-bar-heading">
            <button className="mobile-menu-btn" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu size={24} color="var(--primary-color)" />
            </button>
            <h1>{
              activeTab === 'qc' ? 'Quality Control' :
              activeTab === 'analysis' ? 'Data Analysis' :
              activeTab === 'financeAnalysis' ? 'Financial Analysis' :
              activeTab === 'expenses' ? 'OPEX/REVENUE' :
              activeTab === 'vouchers' ? 'Agent Vouchers' :
              activeTab === 'projects' ? 'Projects' :
              activeTab.charAt(0).toUpperCase() + activeTab.slice(1)
            }</h1>
          </div>
        </header>
        <div className="content-area">
            {canAccessTab(user.role, 'dashboard') ? (
              <KeepAlivePage tab="dashboard" activeTab={activeTab}>
                <Dashboard currentUser={user} onNavigate={handleNavClick} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'analysis') ? (
              <KeepAlivePage tab="analysis" activeTab={activeTab}>
                <DataAnalysis currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'inventory') ? (
              <KeepAlivePage tab="inventory" activeTab={activeTab}>
                <Inventory currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'vouchers') ? (
              <KeepAlivePage tab="vouchers" activeTab={activeTab}>
                <Vouchers currentUser={user} />
              </KeepAlivePage>
            ) : activeTab === 'vouchers' ? (
              <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Agent Vouchers not available</p>
                <p style={{ fontSize: '0.9rem' }}>Your role cannot post completed OPEX from TrashCoin vouchers.</p>
              </div>
            ) : null}
            {canAccessTab(user.role, 'projects') ? (
              <KeepAlivePage tab="projects" activeTab={activeTab}>
                <Projects currentUser={user} />
              </KeepAlivePage>
            ) : activeTab === 'projects' ? (
              <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Projects not available</p>
                <p style={{ fontSize: '0.9rem' }}>Your role cannot open cost centers and campaigns.</p>
              </div>
            ) : null}
            {canAccessTab(user.role, 'production') ? (
              <KeepAlivePage tab="production" activeTab={activeTab}>
                <Production currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'orders') ? (
              <KeepAlivePage tab="orders" activeTab={activeTab}>
                <Orders currentUser={user} />
              </KeepAlivePage>
            ) : activeTab === 'orders' ? (
              <div className="card" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                <p style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>Orders module not available</p>
                <p style={{ fontSize: '0.9rem' }}>Your role does not include access to Sales Orders. Open orders on the dashboard are read-only for your account.</p>
                {canAccessTab(user.role, 'dashboard') && (
                  <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => handleNavClick('dashboard')}>Back to Dashboard</button>
                )}
              </div>
            ) : null}
            {canAccessTab(user.role, 'customers') ? (
              <KeepAlivePage tab="customers" activeTab={activeTab}>
                <Customers currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'leave') ? (
              <KeepAlivePage tab="leave" activeTab={activeTab}>
                <Leave currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'hr') ? (
              <KeepAlivePage tab="hr" activeTab={activeTab}>
                <HR currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'qc') ? (
              <KeepAlivePage tab="qc" activeTab={activeTab}>
                <QualityControl currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'expenses') ? (
              <KeepAlivePage tab="expenses" activeTab={activeTab}>
                <Expenses currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'financeAnalysis') ? (
              <KeepAlivePage tab="financeAnalysis" activeTab={activeTab}>
                <FinanceAnalysis />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'payroll') ? (
              <KeepAlivePage tab="payroll" activeTab={activeTab}>
                <Payroll currentUser={user} />
              </KeepAlivePage>
            ) : null}
            {canAccessTab(user.role, 'attendance') ? (
              <KeepAlivePage tab="attendance" activeTab={activeTab}>
                <Attendance />
              </KeepAlivePage>
            ) : null}
            <KeepAlivePage tab="settings" activeTab={activeTab}>
              <Settings currentUser={user} />
            </KeepAlivePage>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <AppDialogProvider>
      <AppInner />
    </AppDialogProvider>
  );
}

export default App;
